import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { AdminWaitlist } from "@/components/shop/PreorderExtras";
import { useSelection, CheckAll, Check, BulkBar, runBulk, bulkInp } from "@/components/admin/Bulk";

const OrderLine = ({ o, onPaid, s }) => (
  <div className={`flex flex-wrap items-center gap-3 rounded-xl border border-black/10 p-3 text-sm ${s.has(o.id) ? "bg-amber-50" : "bg-white"}`} data-testid={`due-order-${o.id}`}>
    <Check s={s} id={o.id} testid={`due-select-${o.id}`} />
    <Link to={`/ordine/${o.id}`} className="font-semibold text-amber-600 underline">{o.order_number}</Link>
    <span className="text-stone-600">{o.email}</span>
    <span className="text-stone-500">uscita {fmtDate(o.release_date)}</span>
    {o.balance_due > 0 ? (
      <span className="ml-auto flex items-center gap-2">
        Saldo {eur(o.balance_due)} {o.balance_email_sent_at ? <span className="rounded bg-violet-500/15 px-2 text-violet-500">email inviata</span> : o.balance_requested && <span className="rounded bg-rose-500/15 px-2 text-rose-600" data-testid={`balance-email-failed-${o.id}`}>email non inviata, riprova</span>}
        {o.balance_method === "bonifico" && <span className="rounded bg-amber-500/15 px-2 text-amber-700">bonifico/negozio</span>}
        <button onClick={() => onPaid([o.id])} className="rounded-full bg-emerald-600 px-3 py-1 text-xs text-white" data-testid={`balance-paid-${o.id}`}>Saldo ricevuto</button>
      </span>
    ) : <span className="ml-auto font-semibold text-emerald-700">Pagato · pronto da spedire</span>}
  </div>
);

const ProductsTable = ({ products, reload }) => {
  const s = useSelection(products);
  const [date, setDate] = useState("");
  const move = async () => {
    if (!date) return toast.error("Scegli la nuova data di uscita");
    if (await runBulk("/admin/bulk/products", { ids: s.ids, action: "update", changes: { release_date: date } }, "Data di uscita aggiornata")) { s.clear(); reload(); }
  };
  return (
    <section>
      <h3 className="font-display text-xl">Prodotti in preordine</h3>
      <div className="mt-3">
        <BulkBar s={s} entity="products" prefix="preorder-products">
          <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className={bulkInp} data-testid="preorder-bulk-date" />
          <button onClick={move} className="rounded-full bg-black px-3 py-1 text-white" data-testid="preorder-bulk-date-apply">Sposta uscita</button>
        </BulkBar>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
        <table className="w-full min-w-[640px] text-sm" data-testid="preorder-products-table">
          <thead className="bg-[#F3E9D7] text-left text-xs uppercase tracking-wider text-stone-500"><tr><th className="w-10 p-3"><CheckAll s={s} testid="preorder-products-select-all" /></th><th className="p-3">Prodotto</th><th className="p-3">Uscita</th><th className="p-3">Allocazione</th><th className="p-3">Prenotati</th><th className="p-3">Pagamento</th></tr></thead>
          <tbody>
            {products.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-stone-500">Nessun preordine. Crea un prodotto e spunta “Prodotto in preordine” nella scheda Prodotti.</td></tr>}
            {products.map((p) => (
              <tr key={p.id} className={`border-t border-black/5 ${s.has(p.id) ? "bg-amber-50" : ""}`} data-testid={`preorder-row-${p.id}`}>
                <td className="p-3"><Check s={s} id={p.id} testid={`preorder-select-${p.id}`} /></td>
                <td className="p-3 font-semibold">{p.name}</td>
                <td className="p-3">{fmtDate(p.release_date)}</td>
                <td className="p-3">{p.allocation}</td>
                <td className="p-3">{(p.allocation || 0) - p.stock} {p.stock <= 0 && <span className="ml-1 rounded bg-rose-500/15 px-2 text-xs text-rose-600">esaurito</span>}</td>
                <td className="p-3">{p.preorder_mode === "caparra" ? `Caparra ${p.deposit_pct}%` : "Totale anticipato"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const AdminPreorders = ({ onChange }) => {
  const [data, setData] = useState(null);
  const s = useSelection(data?.due || []);
  const [status, setStatus] = useState("");
  const load = () => api.get("/admin/preorders").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const refresh = () => { s.clear(); setStatus(""); load(); onChange?.(); };
  const act = async (url, msg) => {
    try {
      const { data: d } = await api.post(url);
      toast.success(msg(d));
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const paid = async (ids) => { if (await runBulk("/admin/bulk/orders", { ids, action: "balance_paid" }, "Saldo registrato")) refresh(); };
  const applyStatus = async () => {
    if (!status) return toast.error("Scegli il nuovo stato");
    if (status === "annullato" && !window.confirm(`Annullare ${s.ids.length} preordini? Le caparre già versate non vengono rimborsate in automatico.`)) return;
    if (await runBulk("/admin/bulk/orders", { ids: s.ids, action: "status", status }, "Stato aggiornato")) refresh();
  };
  if (!data) return <p className="py-8 text-stone-500">Caricamento...</p>;
  const toRequest = data.need_balance.filter((o) => !o.balance_email_sent_at).length;
  return (
    <div className="space-y-8" data-testid="admin-preorders">
      <ProductsTable products={data.products} reload={load} />
      <section>
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-xl">Arrivati alla data di uscita ({data.due.length})</h3>
          {data.due.length > 0 && <label className="flex items-center gap-1 text-xs"><CheckAll s={s} testid="due-select-all" />Seleziona tutti</label>}
          <button disabled={!data.ready.length} onClick={() => act("/admin/preorders/ship-ready", (d) => d.moved === 1 ? "1 ordine passato in lavorazione" : `${d.moved} ordini passati in lavorazione`)} className="rounded-full bg-black px-4 py-2 text-sm text-white disabled:opacity-40" data-testid="ship-ready-button">Avvia evasione ({data.ready.length})</button>
          <button disabled={!toRequest} onClick={() => act("/admin/preorders/request-balance", (d) => d.requested === 1 ? "Email per il saldo inviata a 1 cliente" : `Email per il saldo inviata a ${d.requested} clienti`)} className="rounded-full border-2 border-black px-4 py-2 text-sm disabled:opacity-40" data-testid="request-balance-button">Richiedi saldi ({toRequest})</button>
        </div>
        <div className="mt-3">
          <BulkBar s={s} entity="preorders" prefix="due">
            <button onClick={() => paid(s.ids)} className="rounded-full bg-emerald-600 px-3 py-1 text-white" data-testid="due-bulk-paid">Saldo ricevuto</button>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={bulkInp} data-testid="due-bulk-status">
              <option value="">Cambia stato…</option>
              <option value="in_lavorazione">In lavorazione</option>
              <option value="spedito">Spedito</option>
              <option value="annullato">Annullato</option>
            </select>
            <button onClick={applyStatus} className="rounded-full bg-black px-3 py-1 text-white" data-testid="due-bulk-apply">Applica</button>
          </BulkBar>
        </div>
        <div className="space-y-2">
          {data.due.length === 0 && <p className="text-sm text-stone-500">Nessun preordine da evadere oggi.</p>}
          {data.due.map((o) => <OrderLine key={o.id} o={o} s={s} onPaid={paid} />)}
        </div>
      </section>
      <AdminWaitlist />
    </div>
  );
};
